/**
 * Navigation Style Constants
 * Base CSS class building blocks for navigation elements
 */

// Color Classes - Used across all navigation elements
export const COLORS = {
  primary: "text-gray-300",
  primaryHover: "hover:text-white",
  secondary: "text-gray-400",
  backgroundHover: "hover:bg-gray-800/50",
  dropdownHover: "hover:bg-gray-700/50",
  dropdownBg: "bg-gray-800/95",
  borderColor: "border-gray-700/50",
  groupHover: "group-hover:text-violet-400",
};

// Typography Classes - Consistent text styling
export const TYPOGRAPHY = {
  base: "text-base",
  weight: "font-medium",
};

// Layout Classes - Spacing, positioning, sizing
export const LAYOUT = {
  // Padding variations
  paddingStandard: "px-3 py-2",
  paddingMobile: "px-3 py-3",
  paddingAuth: "px-4 py-2",
  paddingDropdown: "px-4 py-3",
  paddingMobileDropdown: "px-6 py-2",

  // Display and flexbox
  block: "block",
  flex: "flex",
  flexCenter: "items-center",
  flexBetween: "justify-between",
  fullWidth: "w-full",

  // Positioning
  absolute: "absolute",
  relative: "relative",
  topFull: "top-full",
  leftZero: "left-0",

  // Margins and spacing
  marginTop1: "mt-1",
  marginLeft3: "ml-3",
  marginLeftAuto: "ml-auto",
  spaceY1: "space-y-1",

  // Sizing
  width48: "w-48",
  height4: "h-4",
  height5: "h-5",
  height6: "h-6",
  height8: "h-8",
  height10: "h-10",
  widthFull: "w-full",
  widthAuto: "w-auto",
  heightFull: "h-full",
  minWidth120: "min-w-[120px]",
  maxWidth7xl: "max-w-7xl",

  // Spacing
  padding2: "p-2",
  paddingX4: "px-4",
  paddingY4: "py-4",
  paddingBottom24: "pb-24",
};

// Visual Effects Classes - Borders, shadows, transforms
export const EFFECTS = {
  rounded: "rounded-lg",
  roundedFirst: "first:rounded-t-lg",
  roundedLast: "last:rounded-b-lg",
  shadow: "shadow-lg",
  backdrop: "backdrop-blur",
  border: "border",

  // Transforms and transitions
  transition: "transition-all duration-200",
  transitionColors: "transition-colors",
  transitionOpacity: "transition-opacity duration-200",
  transitionTransform: "transition-transform duration-200",

  // Visibility states
  visible: "opacity-100 visible transform translate-y-0",
  hidden: "opacity-0 invisible transform -translate-y-2",
  mobileVisible: "max-h-96 opacity-100",
  mobileHidden: "max-h-0 opacity-0 overflow-hidden",

  // Icon states
  iconHidden: "opacity-0",
  iconVisible: "group-hover:opacity-100",
  transform180: "transform rotate-180",
};

// Group/State Classes - Interactive states
export const STATES = {
  group: "group",
};

// Header-specific styles
export const HEADER_STYLES = {
  // UserAvatar styles
  avatarSmall: "h-8 w-8 text-base",
  avatarMedium: "h-10 w-10 text-lg",
  avatarLarge: "h-16 w-16 text-2xl",
  avatarRounded: "rounded-full",
  avatarOverflow: "overflow-hidden",
  avatarGradient: "bg-gradient-to-r from-violet-600 to-purple-600",
  avatarBorder: "border-2 border-violet-500",
  avatarText: "text-white font-medium",

  // Logo styles
  logoText:
    "text-2xl font-bold font-sans text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-purple-600 to-violet-900 transition-opacity duration-300 ease-in-out",
  logoImage:
    "h-8 w-auto transition-all duration-300 ease-in-out absolute top-0 left-0",
  logoContainer: "relative min-w-[120px]",
  logoWrapper: "flex-shrink-0 flex items-center",

  // Mobile button styles
  mobileLoginButton:
    "bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg whitespace-nowrap",
  mobileMenuButton:
    "text-gray-300 hover:text-white hover:bg-gray-800/50 p-2 rounded-lg transition-colors h-10 w-10 flex items-center justify-center",

  // Header layout styles
  headerMain:
    "w-full bg-gray-900/95 backdrop-blur border-b border-gray-700/50 sticky top-0 z-50",
  headerContainer: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",
  headerContent: "flex items-center justify-between h-16 relative",
  desktopNav: "hidden lg:flex flex-1 justify-center",
  desktopNavInner: "flex items-center space-x-8",
  desktopRight: "hidden lg:flex items-center gap-4",
  mobileContainer: "lg:hidden flex items-center space-x-3 ml-auto",

  // Mobile navigation styles
  mobileNavContainer: "lg:hidden transition-all duration-300 ease-in-out",
  mobileNavContent: "py-4 space-y-1 border-t border-gray-700/50 pb-24",

  // Dropdown container styles
  dropdownContainer: "relative group",
};
